import React, { useEffect, useState, useContext } from 'react'
import classnames from 'classnames'
import * as qs from 'qs'
import { AmplitudeContext } from '@homebot/shared-util-quartermaster'

import { useListingDisplay } from '../../../../hooks/listing'
import { AppContext } from '../../../../contexts'

import {
  If,
  Unless,
  Text,
  Button,
  ListingStatusHead,
  InlineBulletedList,
  EvaluationList,
} from '../../../../components'

import styles from './LaunchPadRibbon.module.scss'
import { TABS, VALID_TAB_VALUES } from '../../../../constants/display'

import { TextColors } from '../../../../components/Text/Text'

export interface LaunchPadRibbonProps {
  id: string
  className: string
  handleClick: () => void
}

export const LaunchPadRibbon = ({
  id,
  handleClick: expandWidget,
}: LaunchPadRibbonProps) => {
  const { text, color, isError, isFetched, hasUpdatedListingsBotProfile } =
    useListingDisplay()

  const { setActiveTab, setFocusedSettingsField } = useContext(
    AppContext
  ) as any
  const { ampli } = useContext(AmplitudeContext)
  const colorWithFallBack = hasUpdatedListingsBotProfile ? color : 'pink'
  const settledRequest = isFetched

  const [failure, setFailure] = useState(false)

  const { expandModal, focusTab, focusTarget } = qs.parse(
    window.location.search,
    {
      ignoreQueryPrefix: true,
    }
  )

  useEffect(() => {
    if (isError) {
      setFailure(true)
    } else if (settledRequest) {
      setFailure(false)
    }
  }, [isError, settledRequest])

  useEffect(() => {
    if (focusTab && VALID_TAB_VALUES.includes(focusTab)) {
      setActiveTab(focusTab)
    }

    if (focusTarget && focusTab === TABS.SETTINGS) {
      setFocusedSettingsField(focusTarget)
    }

    // only care about presence of param, not value
    if (expandModal) {
      expandWidget()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const expandWidgetAndNavigateToProfiles = () => {
    setActiveTab(TABS.SETTINGS)
    expandWidget()
  }

  const handleViewClick = () => {
    ampli?.insightsViewed()
    hasUpdatedListingsBotProfile
      ? expandWidget()
      : expandWidgetAndNavigateToProfiles()
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.center}>
        <ListingStatusHead
          className={styles.bothead}
          color={colorWithFallBack}
          spin={!settledRequest && !failure}
          fail={failure}
        />
        <div className={styles.text}>
          <Text
            color={colorWithFallBack as TextColors}
            weight='bold'
            className={styles.heading}
          >
            {!settledRequest && 'Analyzing...'}
            {settledRequest && failure && 'Insights failed to load'}
            {hasUpdatedListingsBotProfile &&
              settledRequest &&
              !failure &&
              `${text} Buy`}
            {!hasUpdatedListingsBotProfile &&
              settledRequest &&
              !failure &&
              `Gain financial insights`}
          </Text>
          <Unless condition={settledRequest && !failure}>
            <Text
              className={styles.subText}
              color='white'
              size='xxs'
              weight='semibold'
            >
              {!failure && "We're crunching numbers"}
              {failure &&
                "We couldn't find the data needed for your insights. Try another home."}
            </Text>
          </Unless>
          <If condition={settledRequest && !failure}>
            <Unless condition={hasUpdatedListingsBotProfile}>
              <Text
                className={styles.subText}
                color='white'
                size='xxs'
                weight='semibold'
              >
                Crunch the numbers on this place
              </Text>
            </Unless>
            <If condition={hasUpdatedListingsBotProfile}>
              <InlineBulletedList>
                <EvaluationList className={styles.subText} />
              </InlineBulletedList>
            </If>
          </If>
        </div>
        <If condition={settledRequest && !failure}>
          {/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore */}
          <Button
            id={id}
            className={classnames(
              styles.inspectButton,
              failure && styles.errored
            )}
            fullWidth
            disabled={!settledRequest || failure}
            onClick={handleViewClick}
          >
            <Text element='span' color='inherit' size='s' weight='bold'>
              {hasUpdatedListingsBotProfile ? 'See why' : 'Get started'}
            </Text>
          </Button>
        </If>
      </div>
    </div>
  )
}
